// AttributeLabelsTab.js - Manage attribute labels (autocomplete paths for pricing conditions/formulas)
import {
  React,
  useEffect,
  useState,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PricingCommon';
import { useProductScope } from '../utils/productScope';

const AttributeLabelsTab = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    authFetch('/pricing/attribute-labels')
      .then(data => { setLabels(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch attribute labels:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/pricing/attribute-labels/${editData.id}`, formData, 'PUT');
        // Show cascade info if attribute path was changed
        if (updatedRow.cascaded_updates > 0) {
          alertC(`Saved. Updated ${updatedRow.cascaded_updates} formula/rule reference(s) to use the new attribute path.`);
        }
        setLabels(prev => prev.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/attribute-labels', formData, 'POST');
        setLabels(prev => [...prev, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC(`Failed to save attribute label: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this attribute label?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/attribute-labels/${selectedRow.id}`, null, 'DELETE');
      setLabels(prev => prev.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete attribute label');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'label', headerName: 'Label', width: 250 },
    { field: 'value', headerName: 'Attribute Path', width: 350 },
    { field: 'category', headerName: 'Category', width: 120 },
    { field: 'applies_to', headerName: 'Applies To', width: 120 },
    { field: 'sort_order', headerName: 'Order', width: 70 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Attribute Labels</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Label
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Label
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={handleDelete} disabled={!selectedRow}>
          Delete Label
        </Button>
      </Stack>

      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <span style={{ fontSize: '0.85em', color: '#555' }}>
          Attribute labels define the doorData paths used in pricing conditions and formulas.
          When you change an attribute path here, all formulas and rules using the old path are automatically updated.
        </span>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={labels.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
          getRowClassName={(params) => params.row.id === selectedRow?.id ? 'Mui-selected' : ''}
        />
      )}

      <AttributeLabelDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const AttributeLabelDialog = ({ open, data, onClose, onSave }) => {
  const { pickDefault, optionsForValue } = useProductScope();
  const [formData, setFormData] = useState({
    label: '', value: '', category: 'general', applies_to: 'all', description: '', sort_order: 0, active: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        label: data.label || '',
        value: data.value || '',
        category: data.category || 'general',
        applies_to: data.applies_to || pickDefault('all'),
        description: data.description || '',
        sort_order: data.sort_order || 0,
        active: data.active ?? true
      });
    } else {
      setFormData({ label: '', value: '', category: 'general', applies_to: pickDefault('all'), description: '', sort_order: 0, active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Attribute Label' : 'Add Attribute Label'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth placeholder="e.g., Interior Casing Type" />
          <TextField label="Attribute Path (doorData...)" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} fullWidth placeholder="e.g., doorData.installation.interiorCasing.value" />
          {data?.id && data?.value !== formData.value && formData.value && (
            <Paper sx={{ p: 1, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
              <Typography variant="body2" color="warning.main">
                Changing the attribute path will automatically update all formulas and rules that reference the old path "{data.value}".
              </Typography>
            </Paper>
          )}
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} label="Category">
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="frame">Frame</MenuItem>
              <MenuItem value="color">Color</MenuItem>
              <MenuItem value="glass">Glass</MenuItem>
              <MenuItem value="grill">Grill</MenuItem>
              <MenuItem value="installation">Installation</MenuItem>
              <MenuItem value="shape">Shape</MenuItem>
              <MenuItem value="dimension">Dimension</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Applies To</InputLabel>
            <Select value={formData.applies_to} onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })} label="Applies To">
              {optionsForValue(formData.applies_to).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} sx={{ width: 120 }} />
            <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttributeLabelsTab;
